/** @jsxImportSource @emotion/react */
import _PaymentIQCashier, {
  IPiqCashierApiMethods,
} from 'paymentiq-cashier-bootstrapper';
import { useRef } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useLocation, useNavigate } from 'react-router-dom';
import { useMount, useAsyncFn, useUpdateEffect } from 'react-use';
import { ErrorComponent } from 'components';
import { useKycData } from 'context';
import { PaymentMethod } from 'types/piqPaymentMethod';
import { fetchPIQCashierData } from './api';
import { useDefaultCSS, useDefaultConfig } from './hooks';

interface Props {
  previewId: string;
  product: string;
  qbMerchantId?: string | undefined;
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
  onCancelled?: (data: any) => void;
  onCashierDataFailed?: () => void;
  paymentMethod?: PaymentMethod;
}

export const PaymentIQIFrame = ({
  previewId,
  product,
  qbMerchantId,
  paymentMethod,
  onSuccess,
  onError,
  onCancelled,
  onCashierDataFailed,
}: Props) => {
  const defaultCSS = useDefaultCSS();
  const defaultConfig = useDefaultConfig();
  const cashierRef = useRef<HTMLDivElement | null>(null);
  const kycData = useKycData();
  const location = useLocation();
  const navigate = useNavigate();

  const [{ value, error, loading }, getPIQCashierData] = useAsyncFn(
    () => fetchPIQCashierData(previewId),
    [previewId]
  );

  useMount(() => void getPIQCashierData());

  useUpdateEffect(() => {
    if (!cashierRef.current?.innerHTML) {
      const config = {
        ...defaultConfig,
        environment,
        merchantId,
        userId: userId,
        sessionId: sessionId,
        amount: parseFloat(amount).toFixed(2),
        attributes: {
          preview_id: previewId,
          product: product,
          qb_merchant_id: qbMerchantId,
        },
        showReceipt,
        storeAccount,
        saveAccountOption,
        // autoOpenFirstPaymentMethod: false, // Uncomment this when we have figured out why the paymentMethodSelect callback is blocked in PROD
      };
      if (paymentMethod) {
        config.providerType = paymentMethod.toUpperCase();
        config.autoProcess = true; // Automatically process the payment if a specific payment method is selected
      }
      new _PaymentIQCashier(
        '#cashier',
        config,
        (api: IPiqCashierApiMethods) => {
          api.css(defaultCSS);
          api.on({
            cashierInitLoad: () => console.log('Cashier init load'),
            success: onSuccess
              ? (data) => {
                  onSuccess(data);
                }
              : undefined,
            failure: (errorData) => {
              console.error(errorData);
              if (onError) onError(errorData);
            },
            cancelled: onCancelled ?? ((errorData) => console.error(errorData)),
            isLoading: (data) => console.log('Data is loading', data),
            paymentMethodSelect: (data) => {
              console.log('Payment method selected', data);
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (data?.data?.providerType === 'CREDITCARD') {
                if (!kycData?.address) {
                  if (location.pathname.includes('onramp')) {
                    navigate('/onramp/onboarding/contact-information');
                  } else {
                    navigate('/onboarding/contact-information');
                  }
                }
              }
            },
            paymentMethodPageEntered: (data) =>
              console.log('payment method page entered', data),
            isHidden: () => console.log(' is hidden'),
            isVisible: () => console.log(' is visible'),
            newProviderWindow: (data) =>
              console.log('new provider window', data),
          });
        }
      );
    }
  }, [value]);

  useUpdateEffect(() => {
    if (error && error.statusCode === '404' && onCashierDataFailed)
      onCashierDataFailed();
  }, [error]);

  if (error) {
    return <ErrorComponent error={error} tryAgainAction={getPIQCashierData} />;
  }

  if (loading || !value) return null;

  const {
    userId,
    sessionId,
    amount,
    showReceipt,
    environment,
    merchantId,
    storeAccount,
    saveAccountOption,
  } = value;

  return (
    <div
      id="cashier"
      ref={cashierRef}
      style={{ height: '100%', width: '100%' }}
    />
  );
};
